import { useEffect, useRef, useState } from "react";
import {
  HiOutlineArrowDownTray,
  HiOutlineArrowsRightLeft,
  HiOutlineDocumentText,
  HiOutlineEye,
  HiOutlinePhoto,
  HiOutlineShare,
  HiOutlineTableCells,
  HiOutlineTrash,
} from "react-icons/hi2";
import { Button, CustomModal } from "../../shared";
import { cn } from "../../../lib/cn";
import { FilePreviewSurface } from "../FilePreviewSurface";
import { formatBytes, getPreviewKind } from "../previewUtils";
import { useFileAttachments } from "../../../lib/react-query/fileAttachments";
import { endPoints } from "../../../redux/network/constant";
import { FileAttachmentStatusBadge } from "../FileAttachmentList";
import { getFileTypePalette } from "../../../constants/fileTypeStyles";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
  });
};

const getFileMeta = (file) => {
  const category = file?.category?.toUpperCase() || "OTHER";
  const iconMap = {
    IMAGE: HiOutlinePhoto,
    PDF: HiOutlineDocumentText,
    OFFICE: HiOutlineTableCells,
    TEXT: HiOutlineDocumentText,
    MARKDOWN: HiOutlineDocumentText,
  };

  return {
    Icon: iconMap[category] || HiOutlineDocumentText,
    palette: getFileTypePalette(category),
  };
};

const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-3">
    <span className="text-xs font-medium uppercase tracking-[0.18em] text-grey">{label}</span>
    <span className="max-w-[55%] text-right text-sm text-darkgrey">{value || "—"}</span>
  </div>
);

export const FileDetailsPanel = ({
  file,
  canShare,
  canDelete,
  isOpen,
  onClose,
  onDownload,
  onShare,
  onDelete,
  onMove,
  previewUrl,
  previewError: previewErrorProp,
  onLoadPreview,
  projectId,
}) => {
  const [activeTab, setActiveTab] = useState("details");
  const [previewError, setPreviewError] = useState(null);
  const requestedPreviewForRef = useRef(null);
  const previewRequestRef = useRef(0);

  const { data: workflowRefs } = useFileAttachments(
    projectId ?? null,
    { file_id: file?.id, include_unavailable: true },
    !!projectId && !!file?.id && isOpen,
  );

  useEffect(() => {
    if (isOpen) {
      setActiveTab("details");
      requestedPreviewForRef.current = null;
      setPreviewError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (activeTab !== "preview") {
      requestedPreviewForRef.current = null;
      setPreviewError(null);
      return;
    }
    if (!file || !onLoadPreview) return;
    const previewKind = getPreviewKind(file);
    if (previewKind === "unsupported") return;
    if (previewUrl || requestedPreviewForRef.current === file.id) return;

    const requestId = ++previewRequestRef.current;
    requestedPreviewForRef.current = file.id;
    setPreviewError(null);
    Promise.resolve(onLoadPreview(file)).catch(() => {
      if (requestId === previewRequestRef.current) {
        requestedPreviewForRef.current = null;
        setPreviewError("Failed to load preview");
      }
    });
  }, [activeTab, file, onLoadPreview, previewUrl]);

  if (!file) return null;

  const { Icon, palette } = getFileMeta(file);
  const surfaceError = previewError || previewErrorProp;

  return (
    <CustomModal
      title="File overview"
      open={isOpen}
      close={onClose}
      showPrimaryButton={false}
      showSecondaryButton={false}
      dialogSize="xlarge"
      isDismissable
    >
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-grey">Details</p>
            <h3 className="mt-1 truncate text-base font-semibold text-darkgrey">{file.original_name}</h3>
            <p className="mt-1 text-sm text-grey">
              {file.category || "File"} · {formatBytes(file.size_bytes)}
            </p>
          </div>
          <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border", palette)}>
            <Icon className="h-5 w-5" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outlined"
            color="secondary"
            size="sm"
            rounded="xl"
            onClick={() => setActiveTab("preview")}
            icon={HiOutlineEye}
          >
            Preview
          </Button>
          {onDownload && (
            <Button
              variant="outlined"
              color="secondary"
              size="sm"
              rounded="xl"
              onClick={() => onDownload(file)}
              icon={HiOutlineArrowDownTray}
            >
              Download
            </Button>
          )}
          {onMove && (
            <Button
              variant="outlined"
              color="secondary"
              size="sm"
              rounded="xl"
              onClick={() => onMove?.(file)}
              icon={HiOutlineArrowsRightLeft}
            >
              Move
            </Button>
          )}
          {canShare && (
            <Button
              variant="filled"
              color="secondary"
              size="sm"
              rounded="xl"
              onClick={() => onShare?.(file)}
              icon={HiOutlineShare}
            >
              Share
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outlined"
              color="danger"
              size="sm"
              rounded="xl"
              onClick={() => onDelete?.(file)}
              icon={HiOutlineTrash}
            >
              Delete
            </Button>
          )}
        </div>

        <div className="border-b border-surface-border">
          <div className="flex gap-4 text-sm" role="tablist" aria-label="File details sections">
            <button
              type="button"
              onClick={() => setActiveTab("details")}
              id="file-details-tab-details"
              role="tab"
              aria-selected={activeTab === "details"}
              aria-controls="file-details-panel-details"
              className={cn(
                "relative py-3 font-medium transition-colors",
                activeTab === "details" ? "text-secondary-dark" : "text-grey hover:text-darkgrey"
              )}
            >
              Details
              {activeTab === "details" && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-secondary" />}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("preview")}
              id="file-details-tab-preview"
              role="tab"
              aria-selected={activeTab === "preview"}
              aria-controls="file-details-panel-preview"
              className={cn(
                "relative py-3 font-medium transition-colors",
                activeTab === "preview" ? "text-secondary-dark" : "text-grey hover:text-darkgrey"
              )}
            >
              Preview
              {activeTab === "preview" && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-secondary" />}
            </button>
          </div>
        </div>

        {activeTab === "details" ? (
          <div id="file-details-panel-details" role="tabpanel" aria-labelledby="file-details-tab-details">
            <div className="divide-y divide-surface-border rounded-2xl border border-surface-border bg-white px-4">
              <DetailRow label="Type" value={file.category || "—"} />
              <DetailRow label="Size" value={formatBytes(file.size_bytes)} />
              <DetailRow label="Uploaded by" value={file.uploaded_by_name || "—"} />
              <DetailRow label="Uploaded" value={formatDate(file.created_at)} />
              <DetailRow label="Updated" value={formatDate(file.updated_at || file.created_at)} />
            </div>

            {projectId && workflowRefs && workflowRefs.items.length > 0 && (
              <div className="mt-4 rounded-2xl border border-surface-border bg-white px-4 py-3">
                <h4 className="text-xs font-semibold text-darkgrey mb-2">
                  Used in ({workflowRefs.count})
                </h4>
                <div className="flex flex-col gap-1.5">
                  {workflowRefs.items.map((ref) => (
                    <div key={ref.id} className="flex items-center gap-2 text-sm">
                      <span className="rounded bg-secondary/10 px-1.5 py-0.5 text-[10px] font-semibold text-secondary-dark uppercase">
                        {ref.object_type}
                      </span>
                      <span className="truncate text-darkgrey">
                        {ref.object_title || `#${ref.object_id}`}
                      </span>
                      <span className="ml-auto shrink-0">
                        <FileAttachmentStatusBadge status={ref.status} />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div
            id="file-details-panel-preview"
            role="tabpanel"
            aria-labelledby="file-details-tab-preview"
            className="min-h-[34rem] overflow-hidden rounded-2xl bg-surface"
          >
            <FilePreviewSurface
              file={file}
              previewUrl={previewUrl}
              contentUrl={projectId ? endPoints.ProjectFileContent(projectId, file.id) : undefined}
              error={surfaceError}
              onDownload={() => onDownload?.(file)}
            />
          </div>
        )}
      </div>
    </CustomModal>
  );
};
